import type { TFunction } from "i18next";
import { z } from "zod";
import { PartnerType } from "../types/merchantRegister.types";

const requiredText = (message: string) => z.string().trim().min(1, message);
const optionalUrl = (message: string) =>
  z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || z.url().safeParse(value).success, message);

export const createMerchantAccountSchema = (t: TFunction) =>
  z
    .object({
      firstName: requiredText(t("merchantRegister.validation.firstNameRequired")).min(
        2,
        t("merchantRegister.validation.nameMinLength"),
      ),
      lastName: requiredText(t("merchantRegister.validation.lastNameRequired")).min(
        2,
        t("merchantRegister.validation.nameMinLength"),
      ),
      phoneNumber: z
        .string()
        .trim()
        .min(1, t("merchantRegister.validation.phoneNumberRequired"))
        .regex(/^\d{10,}$/, t("merchantRegister.validation.phoneNumberInvalid")),
      email: z
        .string()
        .trim()
        .min(1, t("merchantRegister.validation.emailRequired"))
        .email(t("merchantRegister.validation.emailInvalid")),
      password: z
        .string()
        .min(1, t("merchantRegister.validation.passwordRequired"))
        .min(8, t("merchantRegister.validation.passwordInvalid")),
      confirmPassword: requiredText(t("merchantRegister.validation.confirmPasswordRequired")),
      agreeToTerms: z
        .boolean()
        .refine((value) => value, t("merchantRegister.validation.agreeToTermsRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("merchantRegister.validation.passwordsNotMatch"),
    });

export const createMerchantVerificationSchema = (t: TFunction) =>
  z.object({
    email: z.string(),
    otp: z.string().trim().length(6, t("merchantRegister.validation.otpRequired")),
  });

export const createMerchantBusinessSchema = (t: TFunction) =>
  z.object({
    logoImage: z.instanceof(File).nullable(),
    businessName: requiredText(t("merchantRegister.validation.businessNameRequired")).min(
      2,
      t("merchantRegister.validation.businessNameMinLength"),
    ),
    businessShortIntro: requiredText(
      t("merchantRegister.validation.businessShortIntroRequired"),
    ).max(500, t("merchantRegister.validation.businessShortIntroMaxLength")),
    description: z.string().optional(),
    businessCategory: requiredText(t("merchantRegister.validation.businessCategoryRequired")),
    partnerType: z
      .nativeEnum(PartnerType)
      .refine(
        (value) => value !== PartnerType.None,
        t("merchantRegister.validation.partnerTypeRequired"),
      ),
    businessUrl: optionalUrl(t("merchantRegister.validation.businessUrlInvalid")),
    addresses: z
      .array(
        z.object({
          address1: requiredText(t("merchantRegister.validation.addressRequired")),
          city: requiredText(t("merchantRegister.validation.cityRequired")),
          state: requiredText(t("merchantRegister.validation.stateRequired")),
          zipcode: z
            .string()
            .trim()
            .min(1, t("merchantRegister.validation.zipcodeRequired"))
            .regex(/^\d{5}(-\d{4})?$/, t("merchantRegister.validation.zipcodeInvalid")),
          latitude: z.number().optional(),
          longitude: z.number().optional(),
        }),
      )
      .min(1, t("merchantRegister.validation.atLeastOneAddress")),
  });
